/**
 * Screen 16 — the participant view.
 *
 * This is the screen the whole product is arranged around. A family planning a funeral opens a link
 * from an email, has no Account, has never seen this application, and is grieving. **Everything
 * here is subordinate to that.**
 *
 * From the mockup: max width 760px, warm background, minimal header, Items as cards, an optional
 * note to the parish, then Submit for review. And the sentence that removes the fear of getting it
 * wrong:
 *
 * > Nothing is final until the parish reviews it — you may return to this link and change your
 * > answers until then.
 *
 * ## What is deliberately not here
 *
 * No navigation, no dashboard, no sign-in prompt, no "create an account to save your progress".
 * Anonymous participation is a first-class path rather than a trial of the real thing, and an
 * upsell here would contradict that — as well as being cruel at a funeral.
 *
 * `hidden` Items are absent because the **server** omits them, not because this screen skips them
 * (06-selection-policy.md). `read_only` Items are shown with their value and the reason, because a
 * family that cannot change the Entrance Hymn should still see what it is.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { ItemValue } from '@pius/contracts';

import { Notice } from '../../components/primitives';
import { editorFor, summarize } from '../planning/renderers';
import {
  ApiError,
  fetchLiturgy,
  fetchPlanning,
  saveItem,
  submitItem,
  type PlanningItemView,
} from '../liturgies/api';
import './participate.css';

export function ParticipantScreen(): React.JSX.Element {
  const { id } = useParams({ from: '/liturgies/$id/participate' });
  const { t } = useTranslation('participate');

  const liturgy = useQuery({ queryKey: ['liturgy', id], queryFn: () => fetchLiturgy(id) });
  const planning = useQuery({ queryKey: ['planning', id], queryFn: () => fetchPlanning(id) });

  const error = [liturgy.error, planning.error].find((e) => e instanceof Error) as
    ApiError | undefined;

  if (error) {
    return (
      <div className="par">
        <main className="par__main">
          <section className="par__card">
            <h1 className="par__title">{t('unavailable.title')}</h1>
            <p className="par__body">{t('unavailable.detail')}</p>
          </section>
        </main>
      </div>
    );
  }

  const detail = liturgy.data;
  const view = planning.data;

  if (!detail || !view) {
    return (
      <div className="par">
        <main className="par__main">
          <p className="par__fine">{t('state.loading', { ns: 'common' })}</p>
        </main>
      </div>
    );
  }

  const editable = detail.is_planning_editable;
  const items = view.sections.flatMap((section) =>
    section.items.map((item) => ({ item, sectionLabel: section.label })),
  );

  // Counted over what they can actually act on. Including read-only Items would show "2 of 9" to
  // somebody who was only ever asked two questions.
  const theirs = items.filter(
    ({ item }) => (item.configuration?.access_mode ?? 'selectable') === 'selectable',
  );
  const answered = theirs.filter(({ item }) => item.value !== null).length;

  return (
    <div className="par">
      {/* Minimal, per the mockup. No navigation: there is nowhere else this reader should go. */}
      <header className="par__head">
        <p className="par__eyebrow">{t('header.eyebrow')}</p>
        <h1 className="par__title">{detail.title}</h1>
        {detail.celebration_local_datetime || detail.place ? (
          <p className="par__when">
            {[formatWhen(detail.celebration_local_datetime), detail.place]
              .filter(Boolean)
              .join(' · ')}
          </p>
        ) : null}
      </header>

      <main className="par__main">
        {editable ? (
          <section className="par__card par__card--warm">
            <p className="par__body par__body--lead">{t('reassurance.lead')}</p>
            <p className="par__fine">{t('reassurance.detail')}</p>
          </section>
        ) : (
          <Notice tone="success">{t('closed')}</Notice>
        )}

        {theirs.length > 0 && editable ? (
          <p className="par__progress">
            {t('progress.answered', { answered, total: theirs.length })}
            {answered < theirs.length ? ` — ${t('progress.no_hurry')}` : ''}
          </p>
        ) : null}

        {items.map(({ item, sectionLabel }) => (
          <ItemCard
            key={item.id}
            liturgyId={id}
            item={item}
            sectionLabel={sectionLabel}
            editable={editable}
            timezone={detail.celebration_timezone}
          />
        ))}

        {items.length === 0 ? (
          <section className="par__card">
            <p className="par__body">{t('empty')}</p>
          </section>
        ) : null}

        <footer className="par__foot">
          <p className="par__fine">{t('footer')}</p>
        </footer>
      </main>
    </div>
  );
}

// ── one item ────────────────────────────────────────────────────────────────

function ItemCard({
  liturgyId,
  item,
  sectionLabel,
  editable,
  timezone,
}: {
  liturgyId: string;
  item: PlanningItemView;
  sectionLabel: string;
  editable: boolean;
  timezone: string | null;
}): React.JSX.Element {
  const { t } = useTranslation('participate');
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<ItemValue | null>(item.value);
  const [note, setNote] = useState(item.note ?? '');

  const configuration = item.configuration;
  const options = configuration?.options ?? [];
  const accessMode = configuration?.access_mode ?? 'selectable';
  const writable = editable && accessMode === 'selectable';

  const Editor = editorFor(item.item_type);

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['planning', liturgyId] });
  };

  // Save and Submit stay two calls, exactly as they are for a parish administrator. The distinction
  // is not an administrative nicety: saving is "this is what I think", submitting is "I am ready
  // for you to look at it", and collapsing them would make every keystroke an act of commitment.
  const save = useMutation({
    mutationFn: (value: ItemValue | null) => saveItem(item.id, value, note.trim() || undefined),
    onSuccess: refresh,
  });

  const submit = useMutation({ mutationFn: () => submitItem(item.id), onSuccess: refresh });

  const error = [save.error, submit.error].find((e): e is ApiError => e instanceof ApiError);

  const submitted = item.state === 'submitted' || item.state === 'under_review';

  return (
    <section className="par__card">
      <p className="par__section">{sectionLabel}</p>
      <h2 className="par__item-title">{item.label}</h2>

      {/* Shown, not hidden. A family that cannot change the Entrance Hymn should still see what it
          is — and be told plainly that the parish chose it, so the absence of a control reads as a
          decision rather than as something broken. */}
      {accessMode === 'read_only' ? (
        <>
          <p className="par__value">
            {summarize(item.value, options) || (
              <span className="par__unset">{t('item.not_yet_set')}</span>
            )}
          </p>
          <p className="par__fine">{t('item.parish_chose')}</p>
        </>
      ) : !writable ? (
        <p className="par__value">
          {summarize(item.value, options) || (
            <span className="par__unset">{t('item.not_set')}</span>
          )}
        </p>
      ) : Editor ? (
        <div className="par__editor">
          <Editor
            itemId={item.id}
            label={item.label}
            value={draft}
            options={options}
            disabled={save.isPending}
            timezone={timezone}
            onChange={(next) => {
              setDraft(next);
              // Saved immediately. Somebody choosing readings for a funeral should not have to
              // discover a Save button, and a lost answer here is a phone call to a grieving family.
              save.mutate(next);
            }}
          />
        </div>
      ) : (
        <p className="par__fine">{t('item.parish_handles')}</p>
      )}

      {item.constraint_explanation ? (
        <p className="par__fine">{item.constraint_explanation}</p>
      ) : null}

      {writable ? (
        <>
          <label className="par__note">
            <span className="par__note-label">{t('item.note_label')}</span>
            <textarea
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onBlur={() => save.mutate(draft)}
              placeholder={t('item.note_placeholder')}
            />
          </label>

          <div className="par__actions">
            {submitted ? (
              <p className="par__status">{t('status.sent')}</p>
            ) : item.is_approved ? (
              <p className="par__status">{t('status.accepted')}</p>
            ) : (
              <button
                type="button"
                className="par__btn"
                disabled={submit.isPending || draft === null}
                onClick={() => submit.mutate()}
              >
                {submit.isPending ? t('action.sending') : t('action.send')}
              </button>
            )}

            {save.isPending ? (
              <span className="par__saving">{t('state.saving', { ns: 'common' })}</span>
            ) : null}
          </div>
        </>
      ) : null}

      {error ? <Notice referenceId={error.correlationId}>{error.message}</Notice> : null}
    </section>
  );
}

/**
 * The celebration's own local time, printed as written.
 *
 * Never converted to the reader's timezone: the value is a wall-clock time in the celebration's
 * place, and a family abroad reading "2pm" must see the same 2pm the parish means.
 */
function formatWhen(local: string | null): string {
  if (!local) return '';

  const [date, time] = local.split('T');
  if (!date) return '';

  return time ? `${date} at ${time.slice(0, 5)}` : date;
}
